<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import { computed, nextTick, onMounted, ref } from 'vue';
import store from "../../state/store";
import '@jobinsjp/vue3-datatable/dist/style.css';
import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
import axios from "axios";
import { authHeader } from "../../helpers/authservice/auth-header";
import Multiselect from '@suadelabs/vue3-multiselect';
import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
import moment from 'moment';
import Swal from 'sweetalert2';
import { required, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { truncate } from 'lodash';

const loading = computed(() => store.state.globals.loading);

const users = computed(() => store.state.user.users);

const title = ref('Edit OVR');

const items = ref([
  {
    text: "Home"
  },
  {
    text: "OVR"
  },
  {
    text: "Edit OVR",
    active: true
  },
])

const search = ref({
  number: '',
  notice_of_violation_number: ''
})

const ref_ovr_number = ref(null);

const ref_new_ovr = ref(null);

const search_results = ref([]);

const selected_result = ref(null);

const is_search = ref(false);

const submitted = ref(false);

const edit = ref({
  number: '',
  notes: '',
  password: '',
  user_id: null
})

const rules = computed(() => ({
    number: {
        required: helpers.withMessage("New OVR # is required", required)
    },
    notes : {
        required: helpers.withMessage("Notes is required", required)
    },
    password: {
      required: helpers.withMessage('Your Password is required', required)
    },
    user_id: {
      required: helpers.withMessage("New OVR Creator/Signature is required", required)
    }
}));

const v$ = useVuelidate(rules, edit);

const searchOVR = async () => {
  if (!search.value.number && !search.value.notice_of_violation_number) return;

  store.dispatch('globals/loading', null, { root: true });
  search_results.value = [];

  try {
    const { data } = await axios.get('/ovr/edit', {
      params: search.value,
      headers: authHeader()
    })

    if (data.data.length) {
      search_results.value = data.data;
    }

    is_search.value = true;
    selected_result.value = null;
    edit.value = {
      number: '',
      notes: '',
      password: '',
      user_id: null
    }
    store.dispatch('globals/loaded', null, { root: true });
  } catch (err) {
    console.log(err);
    is_search.value = false;
    store.dispatch('globals/loaded', null, { root: true });
  }
}

const updateOVR = async () => {
  const confirm = await Swal.fire({
    icon: 'warning',
    title: 'Continue?',
    text: 'Do you want to proceed to update OVR?',
    showCancelButton: true,
    confirmButtonText: 'Yes',
  })
  if (!confirm.isConfirmed) {
    return;
  }

  submitted.value = true
  v$.value.$touch();
  if (v$.value.$invalid) return;

  store.dispatch('globals/loading', null, { root: true });

  try {
    const { data } = await axios.put(`/ovr/edit/${selected_result.value.id}/update`, null, {
      params: {
        ...edit.value,
        user_id: edit.value.user_id.id
      },
      headers: authHeader()
    })

    if (data.data) {
      selected_result.value = data.data;
    }

    submitted.value = false;
    edit.value = {
      number: '',
      notes: '',
      password: '',
      user_id: data.data.user
    }
    if (ref_new_ovr.value) {
      await nextTick();
      ref_new_ovr.value.focus();
    }
    store.dispatch('globals/loaded', null, { root: true });
    Swal.fire("Updated", `Successfully updated!`, "success");
  } catch (err) {
    console.log(err);
    const { message } = err.response.data;
    store.dispatch('globals/loaded', null, { root: true });
    Swal.fire("Error", message, "error");
  }
}

const viewLogs = async (ovr_id) => {
  store.dispatch('globals/loading', null, { root: true });
  is_search.value = false;
  selected_result.value = null;

  try {
    const { data } = await axios.get(`/ovr/edit/${ovr_id}/show`, {
      headers: authHeader()
    })

    if (data.data) {
      selected_result.value = data.data;
      edit.value.user_id = data.data.user
    }

    store.dispatch('globals/loaded', null, { root: true });

    await nextTick();
    ref_new_ovr.value.focus();
  } catch (err) {
    console.log(err);
    store.dispatch('globals/loaded', null, { root: true });
  }
}

onMounted(() => {
  ref_ovr_number.value.focus();
  store.dispatch('user/getUserList');
})
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-xs-12 col-md-6 col-xl-3">
          <b-overlay :show="loading">
            <div class="card">
              <div class="card-header">Search OVR # or NOV # here</div>
              <div class="card-body">
                <form @submit.prevent="searchOVR" action="">
                  <div class="mb-3">
                    <label for="ovr_number" class="form-label">OVR #</label>
                    <input ref="ref_ovr_number" type="text" class="form-control" id="ovr_number" v-model="search.number" placeholder="Search OVR # here...">
                  </div>
                   <div class="mb-4">
                    <label for="notice_of_violation_number" class="form-label">NOV #</label>
                    <input type="text" class="form-control" id="notice_of_violation_number" v-model="search.notice_of_violation_number" placeholder="Search NOV # here...">
                  </div>
                  <div class="mb-3">
                    <button :disabled="!search.notice_of_violation_number && !search.number" type="submit" class="form-control btn btn-primary">
                      <i class="ri ri-search-line align-middle mr-1"></i>
                      Search</button>
                  </div>
                </form>
              </div>
            </div>
          </b-overlay>
        </div>
        <div class="col-xs-12 col-md-6 col-xl-9">
          <b-overlay :show="loading">
            <div v-if="is_search && !selected_result" class="card">
              <div class="card-header">
                <h4 class="p-2">Search result{{ search_results.length > 1 ? 's' : '' }}</h4>
              </div>
              <div  class="card-body">
                 <div class="row">
                    <div class="col-lg-12">
                        <data-table
                            :rows="search_results"
                            striped
                            :loading="loading"
                            filter
                            hoverable
                            >
                            <template #thead>
                                <table-head>OVR #</table-head>
                                <table-head>NOV #</table-head>
                                <table-head>Action</table-head>
                            </template>

                            <template #tbody="{row}">
                                <table-body>{{ row.number ? row.number : 'n/a' }}</table-body>
                                <table-body>{{ row.traffic_record_violation_penalty ? row.traffic_record_violation_penalty.notice_of_violation_number || 'n/a' : 'n/a' }}</table-body>
                                <table-body>
                                  <button class="btn btn-info" @click="viewLogs(row.id)">
                                    <i class="mdi mdi-eye align-middle mr-1"></i> View Logs/Edit
                                  </button>
                                </table-body>
                            </template>

                            <template #empty>
                            <TableBodyCell colspan="3">
                                No result found
                            </TableBodyCell>
                        </template>
                        </data-table>
                    </div>
                  </div>
              </div>
            </div>
            <div v-else-if="!is_search && selected_result" class="card">
              <div class="card-header">
                <h4 class="p-2">View/Edit Logs {{ selected_result.number ? `of "${selected_result.number}"` : '' }}</h4>
              </div>
              <div  class="card-body">
                 <div class="row">
                    <div class="col-lg-12 mb-5">
                         <table class="table table-bordered table-sm table-light m-0">
                            <tbody>
                                <tr>
                                    <td class="no-wrap">NOV #</td>
                                    <td>{{ selected_result.traffic_record_violation_penalty 
                                          ? selected_result.traffic_record_violation_penalty.notice_of_violation_number || 'n/a' 
                                          : 'n/a' 
                                        }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="no-wrap">Current OVR</td>
                                    <td>{{ selected_result.number ? selected_result.number : 'n/a' }}</td>
                                </tr>
                                <tr>
                                    <td class="no-wrap">Approved by</td>
                                    <td>{{ selected_result.user ? selected_result.user.name || 'n/a' : 'n/a' }}</td>
                                </tr>
                                <tr>
                                    <td class="no-wrap">Signature</td>
                                    <td>
                                      <template v-if="selected_result.user && selected_result.user.signature">
                                        <img :src="selected_result.user.signature" :alt="selected_result.user.name" width="120" />
                                      </template>
                                      <template v-else>
                                        n/a
                                      </template>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="no-wrap">New OVR #</td>
                                    <td>
                                      <input ref="ref_new_ovr" :class="{ 'is-invalid': submitted && v$.number.$error }" class="form-control" type="text" v-model="edit.number" placeholder="Enter new OVR number here..."/>
                                      <div
                                          v-for="item of v$.number.$errors"
                                          :key="item.$uid"
                                          class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                      </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="no-wrap">New OVR Creator/Signature</td>
                                    <td>
                                        <multiselect
                                            :class="{ 'is-invalid': submitted && v$.user_id.$error }"
                                            :multiple="false"
                                            v-model="edit.user_id" 
                                            :close-on-select="true"
                                            :options="users"
                                            placeholder="Select User"
                                            label="name"
                                            track-by="id"
                                        />
                                        <div
                                          v-for="item of v$.user_id.$errors"
                                          :key="item.$uid"
                                          class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="no-wrap">Note</td>
                                    <td>
                                      <textarea :class="{ 'is-invalid': submitted && v$.notes.$error }" class="form-control" v-model="edit.notes" placeholder="Enter note here..."></textarea>
                                      <div
                                          v-for="item of v$.notes.$errors"
                                          :key="item.$uid"
                                          class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                      </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="no-wrap">Your Password</td>
                                    <td>
                                      <input :class="{ 'is-invalid': submitted && v$.password.$error }" type="password" class="form-control" v-model="edit.password" placeholder="Enter your password here..." />
                                      <div
                                          v-for="item of v$.password.$errors"
                                          :key="item.$uid"
                                          class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                      </div>
                                    </td>
                                </tr>
                                 <tr>
                                    <td class="no-wrap"></td>
                                    <td>
                                      <button @click.prevent="updateOVR" class="btn btn-info form-control">
                                        <i class="mdi mdi-content-save align-middle mr-1"></i>
                                        Update OVR</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="col-lg-12">
                      <h4>Logs</h4>
                      <data-table
                            :rows="selected_result.ordinance_violation_receipt_logs"
                            striped
                            :loading="loading"
                            filter
                            hoverable
                      >
                          <template #thead>
                              <table-head>Old OVR</table-head>
                              <table-head>New OVR</table-head>
                              <table-head>Approver</table-head>
                              <table-head>User</table-head>
                              <table-head>Note</table-head>
                              <table-head>Date</table-head>
                          </template>

                          <template #tbody="{row}">
                              <table-body>{{ row.old_number ? row.old_number : 'n/a' }}</table-body>
                              <table-body>{{ row.new_number ? row.new_number : 'n/a' }}</table-body>
                              <table-body>{{ row.user_approver ? row.user_approver : 'n/a' }}</table-body>
                              <table-body>{{ row.user ? row.user.name : 'n/a' }}</table-body>
                              <table-body :title="row.notes">{{ row.notes ? truncate(row.notes, { length: 20 }) : 'n/a' }}</table-body>
                              <table-body>{{ row.updated_at ? moment(row.updated).format('YYYY-MM-DD hh:mm A') : 'n/a' }}</table-body>
                          </template>

                          <template #empty>
                            <TableBodyCell colspan="6">
                                No logs yet
                            </TableBodyCell>
                        </template>
                      </data-table>
                    </div>
                  </div>
              </div>
            </div>
            <div v-else-if="!is_search && !selected_result" class="col-12 text-center">
                <b-overlay :show="loading">
                  <div class="card">
                    <div class="card-body">
                      <div class="row justify-content-center">
                        <div class="col-lg-9">
                          <h4 class="mt-4 fw-semibold">Edit OVR</h4>
                          <p class="text-muted mt-3 fs-5"> 
                            Search result will show up here
                          </p>
                        </div>
                      </div>
                      <div class="row justify-content-center mt-5 mb-2">
                        <div class="col-sm-7 col-8">
                          <img src="@/assets/images/file.png" alt="" class="img-fluid" width="400">
                        </div>
                      </div>
                    </div>
                  </div>
                </b-overlay>
              </div>
          </b-overlay>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.no-wrap{
  min-width: fit-content;
  width: 300px;
  white-space: nowrap;
}
th, td {
    background-color: #fff;
}
td{
  padding: 5px;
  text-align: center;
}
</style>
